import {
  CODE128_QUIET_ZONE_MODULES,
  MIN_SCANNABLE_MODULE_DOTS,
  code128Width,
  isEncodableInCode128B,
} from '@sitzmark/core';
import { money } from '../../lib/money.js';
import { LABEL_PRINTER_DPI, LABEL_SIZES, codeFor } from './label-geometry.js';
import type { LabelItem, LabelSize, UnscannableCode } from './label-geometry.js';

/**
 * A label run in the printer's own language (SCOPE §7.1, decision 130).
 *
 * The browser path hands Chrome one HTML page per label, and the Windows
 * driver rasterises each page into its own job — the print-wait-print-wait
 * the shop reported. Through the print agent the run goes as ZPL instead:
 * one format per item, its copies asked for with `^PQ`, the whole run one
 * spooler job that the Zebra prints continuously.
 *
 * **The same label, read the same way.** Name on its own line (two at most),
 * size and color beneath, the barcode across the middle, the code and the
 * price along the foot — the HTML label's order, from the same `LABEL_SIZES`
 * table, so the paper and the arithmetic cannot drift apart.
 *
 * **The barcode is sharper than the browser's.** ZPL draws bars in whole
 * printer dots, so the module width is chosen as the largest whole number of
 * dots that fits the symbol *and* its quiet zone, and every bar keeps its exact
 * ratio. The browser scales an SVG to the label and the driver rounds each bar
 * on its own.
 *
 * **And narrower, for a code that is all digits.** Code 128 subset C packs two
 * digits into one symbol, so an even run of digits is half as wide as in
 * subset B — which is what lets this catalog's 14-digit codes print at three
 * dots a bar on 57 × 32 mm stock instead of one (found by rendering the first
 * run the browser suite sent through the agent). Anything else is subset B.
 * The subset is forced (`>;` or `>:`) rather than left to the printer, so the
 * width is known here and the run dialog can say what will actually print.
 *
 * Roll stock only: the Avery sheet is a page of labels for an office printer,
 * and goes through the browser as before.
 */

/** Sizes that are a roll a thermal printer can take. */
export function zplSupports(size: LabelSize): boolean {
  return size !== 'sheet-30up';
}

export interface ZplRunOptions {
  readonly size: LabelSize;
  readonly showPrice: boolean;
  readonly showBothPrices: boolean;
  /** One label per unit, or one per item (SCOPE §7). */
  readonly perUnit: boolean;
  /** The shop's name across the top; null prints none (FB-1114). */
  readonly storeName?: string | null;
}

const dots = (inches: number): number => Math.round(inches * LABEL_PRINTER_DPI);

/** CSS pixels (96 to the inch) to printer dots, for the type sizes the stock table sets. */
const pxToDots = (px: number): number => Math.round((px / 96) * LABEL_PRINTER_DPI);

/**
 * Field data, safe to send: `^FH\` makes `\` the hex escape, so the three
 * characters ZPL would read as commands or escapes are sent as their codes.
 * Everything else goes as UTF-8 under `^CI28`, so an accent prints as itself.
 */
export function zplText(text: string): string {
  return text.replace(
    /[\\^~]/g,
    (character) => `\\${character.charCodeAt(0).toString(16).toUpperCase().padStart(2, '0')}`,
  );
}

/**
 * Break a name into at most `lines` lines of at most `perLine` characters,
 * at spaces where it can, ending in an ellipsis when it had to stop.
 *
 * ZPL's own field block (`^FB`) prints an overflow *over* its last line, so
 * the wrapping is done here, where it can be tested.
 */
export function wrapName(name: string, perLine: number, lines: number): string[] {
  const words = name.trim().split(/\s+/).filter(Boolean);
  const out: string[] = [];
  let current = '';
  let index = 0;

  while (index < words.length && out.length < lines) {
    const word = words[index]!;
    const next = current === '' ? word : `${current} ${word}`;
    if (next.length <= perLine) {
      current = next;
      index += 1;
      continue;
    }
    if (current === '') {
      // One word longer than a line: cut it.
      current = word.slice(0, perLine);
      words[index] = word.slice(perLine);
    }
    out.push(current);
    current = '';
  }
  if (current !== '' && out.length < lines) out.push(current);

  if (index < words.length && out.length > 0) {
    const last = out[out.length - 1]!;
    out[out.length - 1] = `${last.slice(0, Math.max(0, perLine - 1)).trimEnd()}…`;
  }
  return out;
}

/**
 * What goes in the barcode field, and how many modules wide it prints.
 *
 * Start, data and check symbols are eleven modules each; the stop is thirteen.
 * Subset B is one symbol a character; subset C is one symbol per digit pair.
 */
export function zplSymbol(code: string): { readonly data: string; readonly modules: number } {
  if (/^\d+$/.test(code) && code.length >= 4 && code.length % 2 === 0) {
    return { data: `>;${code}`, modules: 11 * (code.length / 2) + 35 };
  }
  // `>:` starts subset B; a literal `>` in the data is `><`.
  return { data: `>:${code.replace(/>/g, '><')}`, modules: code128Width(code) };
}

/** The largest whole-dot module that fits the symbol and both quiet zones, or 0 when none does. */
export function moduleDotsFitting(code: string, widthDots: number): number {
  const modules = zplSymbol(code).modules + 2 * CODE128_QUIET_ZONE_MODULES;
  // Four is plenty: past it the symbol only gets longer, not easier to read.
  return Math.min(4, Math.floor(widthDots / modules));
}

/** The width the barcode is drawn across on this stock, in dots. */
function innerDots(size: LabelSize): number {
  const geometry = LABEL_SIZES[size];
  return dots(geometry.widthIn) - 2 * dots(geometry.paddingIn[1]);
}

/**
 * The codes in a run that will not print scannably *through the agent* —
 * `unscannableCodes` for this path, worst first, with the whole-dot figure the
 * printer will actually use.
 */
export function unscannableOnAgent(codes: readonly string[], size: LabelSize): UnscannableCode[] {
  return [...new Set(codes)]
    .map((code) => ({
      code,
      moduleDots: isEncodableInCode128B(code) ? moduleDotsFitting(code, innerDots(size)) : null,
    }))
    .filter((entry) => entry.moduleDots === null || entry.moduleDots < MIN_SCANNABLE_MODULE_DOTS)
    .sort((a, b) => (a.moduleDots ?? -1) - (b.moduleDots ?? -1));
}

/** One item's label format. `copies` is how many of it come off the roll. */
export function labelFormat(item: LabelItem, options: ZplRunOptions, copies: number): string {
  const geometry = LABEL_SIZES[options.size];
  const width = dots(geometry.widthIn);
  const height = dots(geometry.heightIn);
  const padY = dots(geometry.paddingIn[0]);
  const padX = dots(geometry.paddingIn[1]);
  const inner = width - 2 * padX;

  // Type: the stock's own size, as the HTML label sets it.
  const nameHeight = pxToDots(geometry.fontSizePx) + 2;
  const detailHeight = pxToDots(geometry.fontSizePx * 0.92) + 2;
  const footHeight = pxToDots(geometry.fontSizePx * 0.92) + 2;
  // Font 0 is proportional; 0.6 of its height is a safe average for capitals.
  const perLine = Math.max(8, Math.floor(inner / (nameHeight * 0.6)));

  const fields: string[] = [];
  let y = padY;

  // The shop's name, small, across the top (FB-1114). The bar code below
  // takes what room is left, as it always has.
  const storeName = options.storeName?.trim() ?? '';
  if (storeName !== '') {
    const storeHeight = pxToDots(geometry.fontSizePx * 0.72) + 2;
    fields.push(
      `^FO${padX},${y}^A0N,${storeHeight},${storeHeight}^FB${inner},1,0,C^FH\\^FD${zplText(storeName.toUpperCase())}^FS`,
    );
    y += storeHeight + 2;
  }

  for (const line of wrapName(item.name, perLine, 2)) {
    fields.push(`^FO${padX},${y}^A0N,${nameHeight},${nameHeight}^FH\\^FD${zplText(line)}^FS`);
    y += nameHeight + 2;
  }
  if (item.detail !== '') {
    fields.push(
      `^FO${padX},${y}^A0N,${detailHeight},${detailHeight}^FH\\^FD${zplText(item.detail)}^FS`,
    );
    y += detailHeight + 2;
  }

  // The barcode takes what is left between the text and the foot.
  const code = codeFor(item);
  const footY = height - padY - footHeight;
  const barTop = y + 4;
  // A millimeter clear above the foot, so the code under the bars reads as text.
  const barHeight = Math.max(20, footY - 8 - barTop);
  const module = isEncodableInCode128B(code) ? moduleDotsFitting(code, inner) : 0;
  if (module > 0) {
    const symbol = zplSymbol(code);
    const x = padX + Math.floor((inner - symbol.modules * module) / 2);
    fields.push(
      `^FO${x},${barTop}^BY${module}^BCN,${barHeight},N,N,N,N^FH\\^FD${zplText(symbol.data)}^FS`,
    );
  }

  fields.push(`^FO${padX},${footY}^A0N,${footHeight},${footHeight}^FH\\^FD${zplText(code)}^FS`);
  if (options.showPrice) {
    const price =
      options.showBothPrices && item.cardPriceCents != null
        ? `${money(item.retailCents)} / ${money(item.cardPriceCents)} card`
        : money(item.retailCents);
    fields.push(
      `^FO${padX},${footY}^A0N,${footHeight},${footHeight}^FB${inner},1,0,R^FH\\^FD${zplText(price)}^FS`,
    );
  }

  return [
    '^XA',
    '^CI28',
    `^PW${width}`,
    `^LL${height}`,
    '^LH0,0',
    ...fields,
    `^PQ${copies}`,
    '^XZ',
  ].join('\n');
}

/** The whole run: one format per item, copies by `^PQ`, items with nothing to print left out. */
export function labelRunZpl(items: readonly LabelItem[], options: ZplRunOptions): string {
  if (!zplSupports(options.size)) {
    throw new Error(
      `${LABEL_SIZES[options.size].label} is sheet stock and prints through the browser.`,
    );
  }
  return items
    .map((item) => ({ item, copies: options.perUnit ? Math.max(0, Math.floor(item.quantity)) : 1 }))
    .filter(({ copies }) => copies > 0)
    .map(({ item, copies }) => labelFormat(item, options, copies))
    .join('\n');
}

/** One label that says what it is, for trying a printer from Settings. */
export function testLabelZpl(size: LabelSize): string {
  return labelRunZpl(
    [
      {
        sku: 'TEST01',
        barcode: null,
        name: 'Test label',
        detail: `${LABEL_SIZES[size].label} · if this scans, labels are ready`,
        quantity: 1,
        retailCents: 0,
      },
    ],
    { size, showPrice: false, showBothPrices: false, perUnit: true },
  );
}
